import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from "@auth0/angular-jwt";
import { BehaviorSubject, Observable } from 'rxjs';
import { API_CONSTANTS } from '../base/api-constants';
import { BaseService } from '../base/base-service';
import { ApiResponse } from '../../models/api-response';
import { Router } from '@angular/router';
import {
  decodeBase64Url,
  decodeJwtPayload,
  permissionsFromJwtPayload,
  PortalUserType,
  userTypeFromJwtPayload,
} from '../../utils/jwt-auth.util';

@Injectable({
  providedIn: 'root'
})

export class AuthenticationService extends BaseService {


  constructor(private httpClient: HttpClient, private router: Router) {
    super(httpClient);
  }

  //Image updates
  private readonly USER_OVERRIDE_KEY = 'user_override';

  private userSubject = new BehaviorSubject<any>(this.buildUser());
  user$ = this.userSubject.asObservable();

  getUserSnapshot() {
    return this.userSubject.value;
  }

  /** called after any profile change */
  patchUser(partial: Partial<any>) {
    const merged = { ...this.getUserSnapshot(), ...partial };

    // persist only what you need (e.g., image)
    const overrides = this.getOverrides();
    const newOverrides = { ...overrides, ...partial };
    localStorage.setItem(this.USER_OVERRIDE_KEY, JSON.stringify(newOverrides));

    this.userSubject.next(merged);
  }

  /** initial user = token payload + overrides */
  private buildUser() {
    const tokenUser = this.getUserDetailsViaToken() || {};
    const overrides = this.getOverrides();
    return { ...tokenUser, ...overrides };
  }

  private getOverrides(): any {
    try {
      return JSON.parse(localStorage.getItem(this.USER_OVERRIDE_KEY) || '{}');
    } catch {
      return {};
    }
  }

  jwtHelper = new JwtHelperService();

  setToken(token: string, refreshToken: string) {
    localStorage.setItem("access_token", token);
    localStorage.setItem("refresh_token", refreshToken);

    this.refreshUserFromToken();
  }

  setAuthorizationToken(token: string) {
    localStorage.setItem("auth_token", token);
  }

  setSessionToken(token: string) {
    sessionStorage.setItem("access_token", token);
  }

  private refreshUserFromToken() {
    this.userSubject.next(this.buildUser());
  }

  getToken(): any {
    const token = localStorage.getItem("access_token");
    if (token) {
      return token;
    }
    else {
      const token = sessionStorage.getItem("access_token");
      if (token) {
        return token;
      }
    }
  }

  RefreshAccessToken(): Observable<HttpEvent<ApiResponse>> {
    const model = {
      refreshToken: localStorage.getItem("refresh_token")
    }
    return this.post(API_CONSTANTS.Auth.RefreshAccessToken, model);
  }

  login(model: any): Observable<HttpEvent<ApiResponse>> {
    return this.post(API_CONSTANTS.Auth.Login, model);
  }

  getProfileDetails(): Observable<HttpEvent<ApiResponse>> {
    return this.get(API_CONSTANTS.Auth.getProfileDetails);
  }

  removeToken(): void {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    sessionStorage.removeItem("access_token");
  }

  /** Portal user type from token (Candidate / Provider / Moderator). */
  getUserType(): PortalUserType | undefined {
    const token = this.getToken();
    if (!token) {
      return undefined;
    }
    const decoded = this.jwtHelper.decodeToken(token) as Record<string, unknown> | null;
    return userTypeFromJwtPayload(decoded);
  }

  /** First portal role from the access token (Candidate / Provider). */
  getUserRole(): string | undefined {
    return this.getUserType();
  }

  getPermissions(): string[] {
    const token = this.getToken();
    if (!token) {
      return [];
    }
    const decoded = this.jwtHelper.decodeToken(token) as Record<string, unknown> | null;
    return permissionsFromJwtPayload(decoded);
  }

  getUserID() {
    const token = this.getToken();
    if (token) {
      const { nameid } = this.jwtHelper.decodeToken(token);
      return nameid;
    }
  }

  getUserFullName() {
    const token = this.getToken();
    if (token) {
      const { name } = this.jwtHelper.decodeToken(token);
      return name;
    }
  }

  getUserEmail() {
    const token = this.getToken();
    if (token) {
      const { email } = this.jwtHelper.decodeToken(token);
      return email;
    }
  }

  /**
   * Returns the numeric user status from the JWT payload, if available.
   * Falls back between common casing conventions.
   *
   * 0 = Pending Verification
   * 1 = Initial Login
   * 2 = Activated
   * 3 = Deactivated
   */
  getUserStatus(): number | undefined {
    const details = this.getUserDetailsViaToken();
    if (!details) {
      return undefined;
    }

    // support multiple possible property names, depending on backend casing
    const raw =
      (details as any).userStatus ??
      (details as any).UserStatus ??
      (details as any).status ??
      undefined;

    if (raw === null || raw === undefined) {
      return undefined;
    }

    const parsed = Number(raw);
    return isNaN(parsed) ? undefined : parsed;
  }

  getUserAuthModel(): string {
    const userDetails = this.getUserDetailsViaToken();
    if (!userDetails) {
      return "False";
    }
    return userDetails.AuthModel;
  }

  isAuthenticated(): boolean {
    try {
      const token = this.getToken();
      if (token && !this.jwtHelper.isTokenExpired(token)) {
        return true;
      }
      return false;
    }
    catch {
      return false;
    }
  }

  isInRole(roleName: string): boolean {
    return this.getUserType() === roleName;
  }

  getUserDetailsViaToken(): any {
    const token = this.getToken();
    if (!token) {
      return undefined;
    }
    return decodeJwtPayload(token) ?? undefined;
  }

  decodeUserAuthorizations(): any {
    const authToken = localStorage.getItem("auth_token");
    if (!authToken) {
      return undefined;
    }
    try {
      const parsed = JSON.parse(decodeBase64Url(authToken));
      return typeof parsed === 'string' ? JSON.parse(parsed) : parsed;
    } catch {
      return undefined;
    }
  }

  //Authentications


  logout() {
    this.removeToken();
    localStorage.removeItem(this.USER_OVERRIDE_KEY);
    this.userSubject.next({});

    this.router.navigate(['/auth/login']);
  }

  //Password reset
  RequestPasswordReset(model: any): Observable<HttpEvent<ApiResponse>> {
    return this.post(API_CONSTANTS.Auth.RequestPasswordReset, model);
  }
  VerifyPasswordResetOTP(model: any): Observable<HttpEvent<ApiResponse>> {
    return this.post(API_CONSTANTS.Auth.VerifyPasswordResetOTP, model);
  }
  UpdateUserPassword(model: any): Observable<HttpEvent<ApiResponse>> {
    return this.post(API_CONSTANTS.Auth.UpdateUserPassword, model);
  }
  ChangePassword(model: any): Observable<HttpEvent<ApiResponse>> {
    return this.post(API_CONSTANTS.Auth.ChangePassword, model);
  }

  //Email management
  RequestEmailChange(email: string): Observable<any> {
    return this.get(API_CONSTANTS.Auth.RequestEmailChange + email);
  }
  VerifyEmailChangeOTP(otp: string): Observable<any> {
    return this.get(API_CONSTANTS.Auth.VerifyEmailChangeOTP + otp);
  }

  ChangeProfileImage(model: any): Observable<any> {
    return this.post_formData(API_CONSTANTS.Auth.ChangeProfileImage, model);
  }


  DeleteUserAccount(): Observable<HttpEvent<ApiResponse>> {
    return this.get(API_CONSTANTS.Auth.DeleteUserAccount);
  }
}
